import {
  MAX_CANVAS_DIMENSION,
  MAX_CANVAS_PIXEL_AREA,
  MAX_LAYOUT_OFFSET_COUNT,
  type Offset2D,
  type TextAlign,
  type TextBaseline,
} from '../project/ProjectDocument.js'
import { MAX_FINITE_FLOAT32 } from '../engine/particles/ParticleBuffer.js'
import { MAX_PARTICLE_COUNT } from '../engine/particles/particleBudget.js'

export const NO_LAYOUT_OWNER = 0xffff_ffff
export const MAX_LAYOUT_REAL_OWNER = NO_LAYOUT_OWNER - 1
export const MAX_LAYOUT_TEXT_LENGTH = 10_000
export const MAX_LAYOUT_FONT_NAME_LENGTH = 256
export const MAX_LAYOUT_FONT_SOURCE_LENGTH = 16 * 1_024 * 1_024
export const MAX_LAYOUT_ERROR_MESSAGE_LENGTH = 4_096
export const MIN_LAYOUT_DENSITY = 1
export const MAX_LAYOUT_DENSITY = 25
export const LAYOUT_TEXT_BASELINES = Object.freeze([
  'top', 'middle', 'alphabetic', 'bottom',
] as const)
export type LayoutTextBaseline = TextBaseline | 'alphabetic'

export interface LayoutSnapshot {
  readonly seed: number
  readonly text: string
  readonly fontName: string
  readonly fontSource?: string
  readonly fontSize: number
  readonly lineHeight: number
  readonly wordSpacing: number
  readonly textAlign: TextAlign
  readonly baseline: LayoutTextBaseline
  readonly density: number
  readonly gridMode: boolean
  readonly width: number
  readonly height: number
  readonly particleBudget: number
  readonly wordOffsets: ReadonlyArray<Readonly<Offset2D>>
  readonly letterOffsets: ReadonlyArray<Readonly<Offset2D>>
  readonly dotOffsets: ReadonlyArray<Readonly<Offset2D>>
}

export interface PackedLayoutSnapshot extends Omit<LayoutSnapshot, 'wordOffsets' | 'letterOffsets' | 'dotOffsets'> {
  readonly wordOffsets: Float32Array
  readonly letterOffsets: Float32Array
  readonly dotOffsets: Float32Array
}

export interface LayoutWordMetadata {
  readonly index: number
  readonly text: string
  readonly lineIndex: number
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly baselineY: number
  /**
   * The exact x where the rasterizer painted this word (canvas textAlign
   * 'right', direction rtl). Overlay text must anchor here — centering in the
   * ownership box drifts from the real glyphs because the box is the
   * advance∪ink union, not the paint position.
   */
  readonly anchorX: number
}

export interface LayoutLetterMetadata {
  readonly index: number
  readonly wordIndex: number
  readonly text: string
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly baselineY: number
}

export interface LayoutTargetBuffers {
  readonly count: number
  readonly x: Float32Array
  readonly y: Float32Array
  readonly size: Float32Array
  readonly wordIndex: Uint32Array
  readonly letterIndex: Uint32Array
  readonly dotIndex: Uint32Array
}

export interface LayoutDotBuffers {
  readonly count: number
  readonly x: Float32Array
  readonly y: Float32Array
  readonly width: Float32Array
  readonly height: Float32Array
  readonly relativeX: Float32Array
  readonly relativeY: Float32Array
  readonly wordIndex: Uint32Array
  readonly letterIndex: Uint32Array
}

export interface LayoutResult {
  readonly targets: LayoutTargetBuffers
  readonly dots: LayoutDotBuffers
  readonly words: readonly LayoutWordMetadata[]
  readonly letters: readonly LayoutLetterMetadata[]
}

export interface LayoutWorkerRequest {
  readonly type: 'layout'
  readonly revision: number
  readonly snapshot: PackedLayoutSnapshot
}

export type LayoutErrorCode = 'unsupported' | 'invalid-input' | 'font-load' | 'scan-failed'

export interface LayoutWorkerSuccessResponse {
  readonly type: 'layout-result'
  readonly revision: number
  readonly result: LayoutResult
}

export interface LayoutWorkerErrorResponse {
  readonly type: 'layout-error'
  readonly revision: number
  readonly code: LayoutErrorCode
  readonly message: string
}

export type LayoutWorkerResponse = LayoutWorkerSuccessResponse | LayoutWorkerErrorResponse

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function assertSafeRevision(value: unknown): asserts value is number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new RangeError('Layout revision must be a non-negative safe integer')
  }
}

function assertFinite(value: unknown, label: string, min: number, max: number): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(`${label} must be finite and within ${min}…${max}`)
  }
}

function assertOwner(value: unknown, label: string): asserts value is number {
  if (
    typeof value !== 'number'
    || !Number.isSafeInteger(value)
    || value < 0
    || value > NO_LAYOUT_OWNER
  ) {
    throw new RangeError(`${label} owner is outside the Uint32 ownership range`)
  }
}

function assertText(value: unknown, label: string, maxLength: number, allowEmpty: boolean): asserts value is string {
  if (
    typeof value !== 'string'
    || (!allowEmpty && value.length === 0)
    || value.length > maxLength
  ) {
    throw new RangeError(`${label} has an invalid length`)
  }
}

function assertFullTypedArray(
  value: unknown,
  constructor: typeof Float32Array | typeof Uint32Array,
  label: string,
): asserts value is Float32Array | Uint32Array {
  if (!(value instanceof constructor)) {
    throw new TypeError(`${label} must use ${constructor.name}`)
  }
  if (!(value.buffer instanceof ArrayBuffer)) {
    throw new TypeError(`${label} must be backed by a transferable ArrayBuffer`)
  }
  if (value.byteOffset !== 0 || value.byteLength !== value.buffer.byteLength) {
    throw new RangeError(`${label} must own its complete ArrayBuffer view`)
  }
}

function assertOffsetBuffer(value: unknown, label: string): asserts value is Float32Array {
  assertFullTypedArray(value, Float32Array, label)
  const offsets = value as Float32Array
  if (offsets.length % 2 !== 0 || offsets.length / 2 > MAX_LAYOUT_OFFSET_COUNT) {
    throw new RangeError(`${label} offset buffer must contain bounded x/y pairs`)
  }
  for (let index = 0; index < offsets.length; index += 1) {
    if (!Number.isFinite(offsets[index])) {
      throw new RangeError(`${label} offsets must be finite`)
    }
  }
}

function assertTextAlign(value: unknown): asserts value is TextAlign {
  if (value !== 'right' && value !== 'center' && value !== 'left') {
    throw new RangeError('Layout text alignment is invalid')
  }
}

function assertTextBaseline(value: unknown): asserts value is LayoutTextBaseline {
  if (!(LAYOUT_TEXT_BASELINES as readonly unknown[]).includes(value)) {
    throw new RangeError('Layout text baseline is invalid')
  }
}

function validatePackedSnapshot(value: unknown): asserts value is PackedLayoutSnapshot {
  if (!isRecord(value)) throw new TypeError('Layout snapshot must be an object')
  assertFinite(value.seed, 'Layout seed', 0, 0xffff_ffff)
  if (!Number.isSafeInteger(value.seed)) throw new RangeError('Layout seed must be an integer')
  assertText(value.text, 'Layout text', MAX_LAYOUT_TEXT_LENGTH, true)
  assertText(value.fontName, 'Layout font name', MAX_LAYOUT_FONT_NAME_LENGTH, false)
  if (value.fontSource !== undefined) {
    assertText(value.fontSource, 'Layout font source', MAX_LAYOUT_FONT_SOURCE_LENGTH, false)
  }
  assertFinite(value.fontSize, 'Layout font size', 1, 1_024)
  assertFinite(value.lineHeight, 'Layout line height', 0.5, 8)
  assertFinite(value.wordSpacing, 'Layout word spacing', 0, 8)
  assertTextAlign(value.textAlign)
  assertTextBaseline(value.baseline)
  assertFinite(value.density, 'Layout density', MIN_LAYOUT_DENSITY, MAX_LAYOUT_DENSITY)
  if (!Number.isInteger(value.density)) throw new RangeError('Layout density must be an integer')
  if (typeof value.gridMode !== 'boolean') throw new TypeError('Layout grid mode must be boolean')
  assertFinite(value.width, 'Layout canvas width', 1, MAX_CANVAS_DIMENSION)
  assertFinite(value.height, 'Layout canvas height', 1, MAX_CANVAS_DIMENSION)
  if (!Number.isSafeInteger(value.width) || !Number.isSafeInteger(value.height)) {
    throw new RangeError('Layout canvas dimensions must be integers')
  }
  if (value.width * value.height > MAX_CANVAS_PIXEL_AREA) {
    throw new RangeError('Layout canvas exceeds the pixel-area budget')
  }
  assertFinite(value.particleBudget, 'Layout particle budget', 0, MAX_PARTICLE_COUNT)
  if (!Number.isSafeInteger(value.particleBudget)) {
    throw new RangeError('Layout particle budget must be an integer')
  }
  assertOffsetBuffer(value.wordOffsets, 'Word')
  assertOffsetBuffer(value.letterOffsets, 'Letter')
  assertOffsetBuffer(value.dotOffsets, 'Dot')
}

function packOffsets(offsets: ReadonlyArray<Readonly<Offset2D>>, label: string): Float32Array {
  const offsetCount = offsets.length
  if (offsetCount > MAX_LAYOUT_OFFSET_COUNT) {
    throw new RangeError(`${label} offset count exceeds the layout cap`)
  }
  const packed = new Float32Array(offsetCount * 2)
  for (let index = 0; index < offsetCount; index += 1) {
    const offset = offsets[index]
    if (offset === undefined) {
      throw new RangeError(`${label} offsets must be finite x/y pairs`)
    }
    const x = offset.x
    const y = offset.y
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      throw new RangeError(`${label} offsets must be finite x/y pairs`)
    }
    packed[index * 2] = x
    packed[index * 2 + 1] = y
  }
  return packed
}

export function packLayoutSnapshot(snapshot: LayoutSnapshot): PackedLayoutSnapshot {
  const seed = snapshot.seed
  const text = snapshot.text
  const fontName = snapshot.fontName
  const fontSource = snapshot.fontSource
  const fontSize = snapshot.fontSize
  const lineHeight = snapshot.lineHeight
  const wordSpacing = snapshot.wordSpacing
  const textAlign = snapshot.textAlign
  const baseline = snapshot.baseline
  const density = snapshot.density
  const gridMode = snapshot.gridMode
  const width = snapshot.width
  const height = snapshot.height
  const particleBudget = snapshot.particleBudget
  const wordOffsets = snapshot.wordOffsets
  const letterOffsets = snapshot.letterOffsets
  const dotOffsets = snapshot.dotOffsets
  const packed: PackedLayoutSnapshot = {
    seed,
    text,
    fontName,
    ...(fontSource === undefined ? {} : { fontSource }),
    fontSize,
    lineHeight,
    wordSpacing,
    textAlign,
    baseline,
    density,
    gridMode,
    width,
    height,
    particleBudget,
    wordOffsets: packOffsets(wordOffsets, 'Word'),
    letterOffsets: packOffsets(letterOffsets, 'Letter'),
    dotOffsets: packOffsets(dotOffsets, 'Dot'),
  }
  validatePackedSnapshot(packed)
  return packed
}

export function validateLayoutRequest(value: unknown): asserts value is LayoutWorkerRequest {
  if (!isRecord(value) || value.type !== 'layout') {
    throw new TypeError('Layout request type is invalid')
  }
  assertSafeRevision(value.revision)
  validatePackedSnapshot(value.snapshot)
}

function assertFloatArray(value: unknown, length: number, label: string): asserts value is Float32Array {
  assertFullTypedArray(value, Float32Array, label)
  const array = value as Float32Array
  if (array.length !== length) throw new RangeError(`${label} buffer length mismatches count`)
  for (let index = 0; index < array.length; index += 1) {
    const scalar = array[index]
    if (scalar === undefined || !Number.isFinite(scalar) || Math.abs(scalar) > MAX_FINITE_FLOAT32) {
      throw new RangeError(`${label} buffer must contain finite Float32 values`)
    }
  }
}

function assertOwnerArray(value: unknown, length: number, label: string): asserts value is Uint32Array {
  assertFullTypedArray(value, Uint32Array, label)
  if ((value as Uint32Array).length !== length) {
    throw new RangeError(`${label} owner buffer length mismatches count`)
  }
}

function assertUniqueBuffers(arrays: readonly (Float32Array | Uint32Array)[]): void {
  const buffers = new Set<ArrayBuffer>()
  for (const array of arrays) {
    if (array.length === 0) continue
    const buffer = array.buffer as ArrayBuffer
    if (buffers.has(buffer)) throw new RangeError('Layout result buffers may not alias')
    buffers.add(buffer)
  }
}

function validateMetadata(
  value: unknown,
  kind: 'word' | 'letter',
  expectedIndex: number,
  wordCount: number,
): void {
  if (!isRecord(value)) throw new TypeError(`Layout ${kind} metadata must be an object`)
  assertOwner(value.index, `${kind} index`)
  if (value.index !== expectedIndex || value.index === NO_LAYOUT_OWNER) {
    throw new RangeError(`Layout ${kind} indices must be contiguous real owners`)
  }
  if (kind === 'letter') {
    assertOwner(value.wordIndex, 'letter word')
    if (value.wordIndex === NO_LAYOUT_OWNER || value.wordIndex >= wordCount) {
      throw new RangeError('Layout letter word reference is invalid')
    }
  }
  assertText(value.text, `Layout ${kind} text`, MAX_LAYOUT_TEXT_LENGTH, true)
  if (kind === 'word') {
    assertFinite(value.lineIndex, 'Layout word line index', 0, MAX_LAYOUT_REAL_OWNER)
    if (!Number.isSafeInteger(value.lineIndex)) throw new RangeError('Layout line index must be an integer')
    assertFinite(value.anchorX, 'Layout word anchorX', -MAX_FINITE_FLOAT32, MAX_FINITE_FLOAT32)
  }
  for (const key of ['x', 'y', 'width', 'height', 'baselineY'] as const) {
    assertFinite(value[key], `Layout ${kind} ${key}`, -MAX_FINITE_FLOAT32, MAX_FINITE_FLOAT32)
  }
  if ((value.width as number) < 0 || (value.height as number) < 0) {
    throw new RangeError(`Layout ${kind} dimensions must be non-negative`)
  }
}

export function validateLayoutResponse(
  value: unknown,
  particleBudget = MAX_PARTICLE_COUNT,
): asserts value is LayoutWorkerResponse {
  if (!isRecord(value)) throw new TypeError('Layout response must be an object')
  assertSafeRevision(value.revision)
  if (value.type === 'layout-error') {
    if (
      value.code !== 'unsupported'
      && value.code !== 'invalid-input'
      && value.code !== 'font-load'
      && value.code !== 'scan-failed'
    ) throw new RangeError('Layout error code is invalid')
    assertText(value.message, 'Layout error message', MAX_LAYOUT_ERROR_MESSAGE_LENGTH, true)
    return
  }
  if (value.type !== 'layout-result' || !isRecord(value.result)) {
    throw new TypeError('Layout response type is invalid')
  }
  const result = value.result
  if (!isRecord(result.targets) || !isRecord(result.dots)) {
    throw new TypeError('Layout result buffers are invalid')
  }
  const targetCount = result.targets.count
  const dotCount = result.dots.count
  if (
    typeof targetCount !== 'number'
    || !Number.isSafeInteger(targetCount)
    || targetCount < 0
    || targetCount > particleBudget
  ) {
    throw new RangeError('Layout target count exceeds the particle budget')
  }
  if (
    typeof dotCount !== 'number'
    || !Number.isSafeInteger(dotCount)
    || dotCount < 0
    || dotCount > MAX_PARTICLE_COUNT
  ) {
    throw new RangeError('Layout dot count is invalid')
  }
  const targets = result.targets as unknown as LayoutTargetBuffers
  const dots = result.dots as unknown as LayoutDotBuffers
  assertFloatArray(targets.x, targetCount, 'Target x')
  assertFloatArray(targets.y, targetCount, 'Target y')
  assertFloatArray(targets.size, targetCount, 'Target size')
  assertOwnerArray(targets.wordIndex, targetCount, 'Target word')
  assertOwnerArray(targets.letterIndex, targetCount, 'Target letter')
  assertOwnerArray(targets.dotIndex, targetCount, 'Target dot')
  assertFloatArray(dots.x, dotCount, 'Dot x')
  assertFloatArray(dots.y, dotCount, 'Dot y')
  assertFloatArray(dots.width, dotCount, 'Dot width')
  assertFloatArray(dots.height, dotCount, 'Dot height')
  assertFloatArray(dots.relativeX, dotCount, 'Dot relative x')
  assertFloatArray(dots.relativeY, dotCount, 'Dot relative y')
  assertOwnerArray(dots.wordIndex, dotCount, 'Dot word')
  assertOwnerArray(dots.letterIndex, dotCount, 'Dot letter')
  const arrays = [
    targets.x, targets.y, targets.size,
    targets.wordIndex, targets.letterIndex, targets.dotIndex,
    dots.x, dots.y, dots.width, dots.height,
    dots.relativeX, dots.relativeY, dots.wordIndex, dots.letterIndex,
  ] as const
  assertUniqueBuffers(arrays)
  if (!Array.isArray(result.words) || !Array.isArray(result.letters)) {
    throw new TypeError('Layout metadata arrays are invalid')
  }
  if (result.words.length > MAX_LAYOUT_OFFSET_COUNT || result.letters.length > MAX_LAYOUT_OFFSET_COUNT) {
    throw new RangeError('Layout metadata arrays exceed the ownership cap')
  }
  for (let index = 0; index < result.words.length; index += 1) {
    validateMetadata(result.words[index], 'word', index, result.words.length)
  }
  for (let index = 0; index < result.letters.length; index += 1) {
    validateMetadata(result.letters[index], 'letter', index, result.words.length)
  }
  const words = result.words as unknown as readonly LayoutWordMetadata[]
  const letters = result.letters as unknown as readonly LayoutLetterMetadata[]
  for (let index = 0; index < dotCount; index += 1) {
    const wordOwner = dots.wordIndex[index] ?? NO_LAYOUT_OWNER
    const letterOwner = dots.letterIndex[index] ?? NO_LAYOUT_OWNER
    if (wordOwner >= words.length || letterOwner >= letters.length) {
      throw new RangeError('Layout dot owner reference is invalid')
    }
    if (letters[letterOwner]?.wordIndex !== wordOwner) {
      throw new RangeError('Layout dot owner relationship is inconsistent')
    }
  }
  for (let index = 0; index < targetCount; index += 1) {
    const wordOwner = targets.wordIndex[index] ?? NO_LAYOUT_OWNER
    const letterOwner = targets.letterIndex[index] ?? NO_LAYOUT_OWNER
    const dotOwner = targets.dotIndex[index] ?? NO_LAYOUT_OWNER
    if (wordOwner !== NO_LAYOUT_OWNER && wordOwner >= words.length) {
      throw new RangeError('Layout target word owner reference is invalid')
    }
    if (letterOwner !== NO_LAYOUT_OWNER) {
      if (letterOwner >= letters.length || letters[letterOwner]?.wordIndex !== wordOwner) {
        throw new RangeError('Layout target letter owner relationship is inconsistent')
      }
    }
    if (dotOwner !== NO_LAYOUT_OWNER) {
      if (
        dotOwner >= dotCount
        || dots.wordIndex[dotOwner] !== wordOwner
        || dots.letterIndex[dotOwner] !== letterOwner
      ) {
        throw new RangeError('Layout target dot owner relationship is inconsistent')
      }
    }
  }
}

function addTransferBuffer(
  transfers: ArrayBuffer[],
  seen: Set<ArrayBuffer>,
  view: ArrayBufferView,
): void {
  if (!(view.buffer instanceof ArrayBuffer)) {
    throw new TypeError('Layout transfer views must use ArrayBuffer, not SharedArrayBuffer')
  }
  if (view.byteOffset !== 0 || view.byteLength !== view.buffer.byteLength) {
    throw new RangeError('Layout transfer views must own their complete ArrayBuffer')
  }
  if (view.byteLength === 0) return
  if (!seen.has(view.buffer)) {
    seen.add(view.buffer)
    transfers.push(view.buffer)
  }
}

export function getLayoutRequestTransferList(request: LayoutWorkerRequest): ArrayBuffer[] {
  const transfers: ArrayBuffer[] = []
  const seen = new Set<ArrayBuffer>()
  addTransferBuffer(transfers, seen, request.snapshot.wordOffsets)
  addTransferBuffer(transfers, seen, request.snapshot.letterOffsets)
  addTransferBuffer(transfers, seen, request.snapshot.dotOffsets)
  return transfers
}

export function getLayoutResponseTransferList(response: LayoutWorkerResponse): ArrayBuffer[] {
  if (response.type === 'layout-error') return []
  const transfers: ArrayBuffer[] = []
  const seen = new Set<ArrayBuffer>()
  const targets = response.result.targets
  const dots = response.result.dots
  for (const view of [
    targets.x, targets.y, targets.size, targets.wordIndex, targets.letterIndex, targets.dotIndex,
    dots.x, dots.y, dots.width, dots.height, dots.relativeX, dots.relativeY,
    dots.wordIndex, dots.letterIndex,
  ]) addTransferBuffer(transfers, seen, view)
  return transfers
}
